/**
 * useTemplateSelection — template selection, full-detail fetch, planner
 * navigation, and the ?template= deep-link contract (read AND write).
 *
 * Extracted from BlogTemplatesPage (Phase 1 decomposition). Behavior preserved:
 * - Detail fetch on selection change (clears to null when deselected or on
 *   fetch error).
 * - Read preselect: ?template=<id> or ?recipe=<id> selects the matching
 *   template once the list loads (forward-compat read; Phase 0 addition).
 * - Use Template navigates to
 *   /content/planner/wizard?template=<id>&entry=have-keywords (exact).
 *
 * ACCEPTANCE-CRITICAL ADDITION (lead ruling, Phase 1 acceptance list):
 * ?template= WRITE-on-selection. Selecting a template syncs
 * ?template=<id> into the URL with { replace: true } so selection does not
 * spam history; clearing the selection removes the param. Only ?template= is
 * written — ?recipe= remains read-only forward-compat. On clear, BOTH params
 * are removed: leaving ?recipe= in place would re-trigger the read-preselect
 * effect and make clearing impossible for recipe deep links.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { blogTemplatesService, BlogTemplate, BlogTemplateSummary } from '@/services/blogTemplatesService';

export interface UseTemplateSelectionResult {
  selectedPreviewTemplate: BlogTemplateSummary | null;
  fullTemplateData: BlogTemplate | null;
  isLoadingPreview: boolean;
  /** Selects a template and writes ?template=<id> to the URL (replace). */
  handleSelectTemplate: (template: BlogTemplateSummary) => void;
  /** Clears the selection and removes ?template=/?recipe= from the URL. */
  clearSelection: () => void;
  /** Navigates to the planner wizard with the template pre-selected. */
  handleUseTemplate: (template: BlogTemplateSummary) => void;
  /** Exposed for the inline-save flow, which refetches and replaces the
   *  full detail object after an update (page behavior preserved). */
  setFullTemplateData: (template: BlogTemplate | null) => void;
  /** Re-runs the detail fetch for the current selection (error-state Retry;
   *  re-selecting the same id would not retrigger the effect). */
  retryFetch: () => void;
}

export function useTemplateSelection(
  templates: BlogTemplateSummary[],
): UseTemplateSelectionResult {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedPreviewTemplate, setSelectedPreviewTemplate] = useState<BlogTemplateSummary | null>(null);
  const [fullTemplateData, setFullTemplateData] = useState<BlogTemplate | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [fetchNonce, setFetchNonce] = useState(0);

  // Fetch full template data when selection changes
  const selectedPreviewId = selectedPreviewTemplate?.id ?? null;
  useEffect(() => {
    if (!selectedPreviewId) {
      setFullTemplateData(null);
      return;
    }

    const fetchFullTemplate = async () => {
      setIsLoadingPreview(true);
      try {
        const fullTemplate = await blogTemplatesService.get(selectedPreviewId);
        setFullTemplateData(fullTemplate);
      } catch (err) {
        console.error('Failed to fetch template details:', err);
        setFullTemplateData(null);
      } finally {
        setIsLoadingPreview(false);
      }
    };

    fetchFullTemplate();
  }, [selectedPreviewId, fetchNonce]);

  // Deep-link READ: preselect a template from ?template=<id> (or ?recipe=<id>)
  useEffect(() => {
    const templateId = searchParams.get('template') || searchParams.get('recipe');
    if (!templateId || templates.length === 0) return;
    const match = templates.find((t) => t.id === templateId);
    if (match) {
      setSelectedPreviewTemplate(match);
    }
  }, [searchParams, templates]);

  // Deep-link WRITE: selection syncs ?template=<id>; clearing removes it.
  const handleSelectTemplate = useCallback((template: BlogTemplateSummary) => {
    setSelectedPreviewTemplate(template);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('template', template.id);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const clearSelection = useCallback(() => {
    setSelectedPreviewTemplate(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('template');
      next.delete('recipe');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleUseTemplate = useCallback((template: BlogTemplateSummary) => {
    // Quick Create v2 (#row3): open the SAME Content Planner modal (open=true)
    // with the template preselected on the Quick Create path — instead of the
    // separate, mostly-dormant /content/planner/wizard route (the seam that made
    // "Use this template" feel broken). ContentPlannerPage auto-opens on
    // ?open=true; the wizard reads ?template + ?step=keywords.
    navigate(`/content/planner?open=true&template=${template.id}&step=keywords`);
  }, [navigate]);

  return {
    selectedPreviewTemplate,
    fullTemplateData,
    isLoadingPreview,
    handleSelectTemplate,
    clearSelection,
    handleUseTemplate,
    setFullTemplateData,
    retryFetch: useCallback(() => setFetchNonce((n) => n + 1), []),
  };
}
